import { useParams, useNavigate } from 'react-router-dom';
import { useTemplate, useUpdateTemplate, useCreateTemplate, useDeleteTemplate, useCluvioDashboards } from '@/api/hooks';
import { useEffect, useMemo, useState } from 'react';
import { FilterListEditor, ReviewFiltersDialog, ReencodeDialog } from '@/features/templates/template-filter-editor';
import { lintTemplateFilters, diffFilters } from '@/lib/template-lint';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { Badge } from '@/components/Badge';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Dialog } from '@/components/Dialog';
import { Skeleton, EmptyState, Field } from '@/components/primitives';
import { Tooltip } from '@/components/Tooltip';
import { TENANT_TYPES } from '@/lib/cluvio';
import { toast } from 'react-hot-toast';
import { ArrowLeft, Copy, Trash2, Save, Search, AlertCircle, AlertTriangle, ExternalLink, RefreshCw } from 'lucide-react';

export function TemplateDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: template, isLoading, error, refetch } = useTemplate(id || '');
  const updateMutation = useUpdateTemplate();
  const createMutation = useCreateTemplate();
  const deleteMutation = useDeleteTemplate();

  const [edits, setEdits] = useState<Record<string, string | boolean | number>>({});
  const [dashboardSearch, setDashboardSearch] = useState('');
  const [varDraft, setVarDraft] = useState<Record<string, any>[]>([]);
  const [fixedDraft, setFixedDraft] = useState<Record<string, any>[]>([]);
  const [filtersDirty, setFiltersDirty] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reencodeOpen, setReencodeOpen] = useState(false);
  const [cloneOpen, setCloneOpen] = useState(false);
  const [cloneName, setCloneName] = useState('');
  const [cloneType, setCloneType] = useState('');

  const { data: cluvioDashboards } = useCluvioDashboards();
  const dashboardFilters = useMemo(() => {
    const cluvioId = (template as unknown as Record<string, any> | undefined)?.cluvioDashboard?.id;
    return cluvioDashboards?.find((d) => d.id === cluvioId)?.filters ?? [];
  }, [cluvioDashboards, template]);

  const controlTypeByFilterId = useMemo(
    () => Object.fromEntries(dashboardFilters.map((f) => [f.id, f.controlType ?? ''])),
    [dashboardFilters]
  );

  const filterIssues = useMemo(() => lintTemplateFilters(fixedDraft, varDraft), [fixedDraft, varDraft]);

  // Initialize filter drafts when a template loads; untouched on refetches
  // of the same template (edits survive background invalidation).
  useEffect(() => {
    if (template) {
      setVarDraft((template.variableFilters ?? []) as unknown as Record<string, any>[]);
      setFixedDraft((template.fixedFilters ?? []) as unknown as Record<string, any>[]);
      setFiltersDirty(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template?.id]);

  if (isLoading) {
    return (
      <div className="space-y-5 max-w-3xl mx-auto">
        <Skeleton className="h-7 w-64" />
        <Card className="p-5 space-y-3">
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
        </Card>
        <div className="grid gap-4 md:grid-cols-2">
          <Card className="p-5"><Skeleton className="h-40 w-full" /></Card>
          <Card className="p-5"><Skeleton className="h-40 w-full" /></Card>
        </div>
      </div>
    );
  }

  if (error || !template) {
    return (
      <div className="max-w-3xl mx-auto">
        <EmptyState
          icon={AlertCircle}
          title="Failed to load template"
          description={error?.message}
          action={
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              Retry
            </Button>
          }
        />
      </div>
    );
  }

  const changed = (key: string) => (key in edits ? edits[key] : (template as unknown as Record<string, unknown>)[key]);
  const hasEdits = Object.keys(edits).length > 0;

  const handleUpdate = async () => {
    if (!id) return;
    try {
      await updateMutation.mutateAsync({
        id,
        input: {
          ...edits,
          id,
          ...(filtersDirty ? { fixedFilters: fixedDraft, variableFilters: varDraft } : {}),
        },
      });
      toast.success('Template updated');
      setEdits({});
      setFiltersDirty(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Update failed');
    }
  };

  const openClone = () => {
    setCloneName(`Copy of ${template.name}`);
    setCloneType((template as unknown as Record<string, unknown>).typeOfTenantID as string);
    setCloneOpen(true);
  };

  const handleClone = async () => {
    try {
      const { id: _id, createdAt, updatedAt, typeOfTenant, dashboards, ...rest } = template as unknown as Record<string, unknown>;
      const tenantType = cloneType.toUpperCase().replace(/-/g, '_');
      await createMutation.mutateAsync({
        ...rest,
        name: cloneName || `Copy of ${template.name}`,
        typeOfTenantID: cloneType,
        tenantType: tenantType === 'SELF_SERVE' ? 'NONE' : tenantType,
      });
      toast.success('Template cloned');
      setCloneOpen(false);
      navigate('/templates');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Clone failed');
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    try {
      await deleteMutation.mutateAsync(id);
      toast.success('Template deleted');
      navigate('/templates');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Delete failed');
    }
  };

  return (
    <div className="space-y-5 max-w-3xl mx-auto">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Tooltip label="Back to templates" side="bottom">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} aria-label="Back">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Tooltip>
          <div className="min-w-0">
            <h1 className="text-xl font-semibold tracking-tight truncate">{template.name}</h1>
            <p className="text-xs text-muted-foreground">Template details and usage</p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {(() => {
            const cluvioId = (template as unknown as Record<string, any>).cluvioDashboard?.id;
            if (!cluvioId) return null;
            return (
              <Tooltip label="Edit the source dashboard in Cluvio" side="bottom">
                <a
                  href={`https://app.cluvio.com/dashboards/${cluvioId}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center rounded-md border border-input bg-background shadow-card h-8 w-8 transition-colors hover:bg-accent hover:text-accent-foreground"
                >
                  <ExternalLink className="h-4 w-4" />
                </a>
              </Tooltip>
            );
          })()}
          <Button variant="outline" size="sm" onClick={openClone}>
            <Copy className="h-4 w-4 mr-2" />
            Clone
          </Button>
          <Button variant="ghost" size="sm" className="text-destructive hover:bg-destructive/10" onClick={() => setConfirmDelete(true)}>
            <Trash2 className="h-4 w-4 mr-2" />
            Delete
          </Button>
        </div>
      </div>

      <Card className="p-5 space-y-4 animate-enter">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Name">
            <Input
              value={String(changed('name') ?? '')}
              onChange={(e) => setEdits({ ...edits, name: e.target.value })}
            />
          </Field>
          <Field label="Default Dashboard Name">
            <Input
              value={String(changed('defaultDashboardName') ?? '')}
              onChange={(e) => setEdits({ ...edits, defaultDashboardName: e.target.value })}
            />
          </Field>
          <Field label="Description" className="sm:col-span-2">
            <Input
              value={String(changed('description') ?? '')}
              onChange={(e) => setEdits({ ...edits, description: e.target.value })}
            />
          </Field>
          <Field label="Default Sort Order">
            <Input
              type="number"
              value={String(changed('defaultDashboardSortOrder') ?? 0)}
              onChange={(e) => setEdits({ ...edits, defaultDashboardSortOrder: Number(e.target.value) })}
            />
          </Field>
          <div className="space-y-2.5">
            <label className="flex items-center gap-2.5 text-sm cursor-pointer">
              <input
                type="checkbox"
                className="h-4 w-4 accent-primary"
                checked={Boolean(changed('defaultIsFilterOpen') ?? false)}
                onChange={(e) => setEdits({ ...edits, defaultIsFilterOpen: e.target.checked })}
              />
              Filter open by default
            </label>
            <label className="flex items-center gap-2.5 text-sm cursor-pointer">
              <input
                type="checkbox"
                className="h-4 w-4 accent-primary"
                checked={Boolean(changed('displayFilterOpen') ?? false)}
                onChange={(e) => setEdits({ ...edits, displayFilterOpen: e.target.checked })}
              />
              Collapsible filter section
            </label>
          </div>
        </div>

        <div className="flex justify-end pt-1">
          <Button
            onClick={() => (filtersDirty ? setReviewOpen(true) : handleUpdate())}
            disabled={(!hasEdits && !filtersDirty) || updateMutation.isPending}
            size="sm"
          >
            <Save className="h-4 w-4 mr-2" />
            {updateMutation.isPending ? 'Saving…' : hasEdits || filtersDirty ? 'Save Changes' : 'No Changes'}
          </Button>
        </div>
      </Card>

      {filtersDirty && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-400 animate-enter">
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>
            {template.dashboards?.items?.length ?? 0}
            {template.dashboards?.nextToken ? '+' : ''} existing dashboard
            {(template.dashboards?.items?.length ?? 0) === 1 ? '' : 's'} will keep their current configuration — filter
            edits apply only to dashboards created from this template in the future.
          </span>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        <FilterListEditor
          kind="fixed"
          items={fixedDraft}
          onChange={(v) => {
            setFixedDraft(v);
            setFiltersDirty(true);
          }}
          availableFilters={dashboardFilters}
          excludeKeys={varDraft.map((f) => f.key)}
          controlTypeByFilterId={controlTypeByFilterId}
          issues={filterIssues}
        />
        <FilterListEditor
          kind="variable"
          items={varDraft}
          onChange={(v) => {
            setVarDraft(v);
            setFiltersDirty(true);
          }}
          availableFilters={dashboardFilters}
          excludeKeys={fixedDraft.map((f) => f.key)}
          controlTypeByFilterId={controlTypeByFilterId}
          issues={filterIssues}
        />
      </div>

      <Card className="p-5 animate-enter">
        <div className="flex items-center gap-2 mb-3">
          <h3 className="text-sm font-semibold">
            Dashboards <Badge variant="secondary" className="ml-1.5">{template.dashboards?.items?.length ?? 0}</Badge>
          </h3>
          {(template.dashboards?.items?.length ?? 0) > 0 && (
            <Tooltip label="Recreate all dashboards from the current template definition" side="bottom">
              <Button variant="ghost" size="sm" className="ml-auto h-7 text-xs" onClick={() => setReencodeOpen(true)}>
                <RefreshCw className="h-3.5 w-3.5 mr-1" />
                Re-encode
              </Button>
            </Tooltip>
          )}
        </div>
        <div className="relative mb-2">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={dashboardSearch}
            onChange={(e) => setDashboardSearch(e.target.value)}
            placeholder="Search dashboards..."
            className="pl-9 h-8 text-xs"
          />
        </div>
        {template.dashboards?.items?.length > 0 ? (
          <div className="space-y-1 max-h-64 overflow-auto pr-1">
            {template.dashboards.items
              .filter((d) => d.name.toLowerCase().includes(dashboardSearch.toLowerCase()))
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((d) => (
                <div key={d.id} className="flex items-center justify-between gap-2 text-xs rounded-md px-2 py-1.5 hover:bg-muted/60 transition-colors">
                  <span className="truncate font-medium">{d.name}</span>
                  <span className="text-muted-foreground truncate">{d.tenantID}</span>
                </div>
              ))}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground py-4 text-center">No dashboards using this template</p>
        )}
      </Card>

      <ConfirmDialog
        open={confirmDelete}
        title={`Delete "${template.name}"?`}
        description="This removes the template definition. Dashboards already created from it are not affected."
        confirmLabel="Delete"
        destructive
        loading={deleteMutation.isPending}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(false)}
      />

      <ReviewFiltersDialog
        open={reviewOpen}
        templateName={template.name}
        fixedDiff={diffFilters((template.fixedFilters ?? []) as unknown as Record<string, any>[], fixedDraft)}
        variableDiff={diffFilters((template.variableFilters ?? []) as unknown as Record<string, any>[], varDraft)}
        pending={updateMutation.isPending}
        onConfirm={() => {
          setReviewOpen(false);
          handleUpdate();
        }}
        onCancel={() => setReviewOpen(false)}
      />

      <ReencodeDialog
        template={template as unknown as Record<string, any>}
        open={reencodeOpen}
        onClose={() => setReencodeOpen(false)}
        onDone={() => {
          setReencodeOpen(false);
          refetch();
        }}
      />

      <Dialog
        open={cloneOpen}
        title="Clone Template"
        onClose={() => setCloneOpen(false)}
        busy={createMutation.isPending}
        footer={
          <>
            <Button variant="outline" size="sm" onClick={() => setCloneOpen(false)} disabled={createMutation.isPending}>
              Cancel
            </Button>
            <Button size="sm" onClick={handleClone} disabled={createMutation.isPending}>
              {createMutation.isPending ? 'Cloning…' : 'Clone'}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Field label="New template name">
            <Input value={cloneName} onChange={(e) => setCloneName(e.target.value)} autoFocus />
          </Field>
          <Field label="Tenant type ID" hint="Which tenant directory the clone belongs to.">
            <Select value={cloneType} onChange={(e) => setCloneType(e.target.value)}>
              {!TENANT_TYPES.includes(cloneType as (typeof TENANT_TYPES)[number]) && cloneType && (
                <option value={cloneType}>{cloneType} (current)</option>
              )}
              {TENANT_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </Select>
          </Field>
        </div>
      </Dialog>
    </div>
  );
}
